'use client';
import { IconChevronSend } from '@vs-shop/ui/icons';
import {
  NewsletterBand,
  NewsletterBandConsent,
  NewsletterBandCopy,
  NewsletterBandField,
  NewsletterBandForm,
  NewsletterBandInput,
  NewsletterBandInputVariant,
  NewsletterBandNotice,
  NewsletterBandNoticeVariant,
  NewsletterBandSubmit,
  NewsletterBandSubmitVariant,
  NewsletterBandSubtitle,
  NewsletterBandTitle,
} from '@vs-shop/commerce-ui/newsletterBand';
import { captureCause } from '../../../../shared/utils/sentry';
import { Exit } from 'effect';
import { useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Url } from '../../../../route/router';

import Link from '@vs-shop/image-link/Link';

import effectTsResolver from '@vs-shop/engine/adapter/effect-hook-form/effectTsResolver';
import { subscribeNewsletterAtomFn } from '../../../../effect/atom/email/subscribeNewsletterAtomFn';
import type { NewsletterFormData } from '@vs-shop/schemas/authForms';
import { NewsletterFormSchema } from '@vs-shop/schemas/authForms';
import { useAtomSet } from '@effect/atom-react';

/**
 * rcprofi's newsletter band: the shared section composed around this tenant's
 * subscribe flow.
 *
 * Everything about subscribing stays here — the address schema, the resolver,
 * the atom, the error capture, the copy and the link to the privacy terms. The
 * band contributes the shape.
 *
 * rcprofi runs it at its own measure beside the support block; RacingLine runs
 * the same parts full width. That is `className` and arrangement, not a prop.
 */
export default function FooterNewsletterCTA() {
  const intl = useIntl();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isPending, startTransition] = useTransition();
  const subscribeNewsletter = useAtomSet(subscribeNewsletterAtomFn, { mode: 'promiseExit' });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewsletterFormData>({
    resolver: effectTsResolver(NewsletterFormSchema),
    mode: 'onBlur',
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = (data: { email: string }) => {
    setErrorMessage(null);

    startTransition(async () => {
      const result = await subscribeNewsletter(data.email);

      Exit.match(result, {
        onFailure: (cause) => {
          captureCause(cause, {
            component: 'FooterNewsletterCTA',
            action: 'Newsletter subscription failed',
            additionalData: {
              email: data.email,
            },
          });

          setErrorMessage(
            intl.formatMessage({
              id: 'component.server.page.footer.footerNewsletterCTA.apiError',
              defaultMessage: 'Došlo k chybě při přihlášení k newsletteru',
            })
          );
        },
        onSuccess: () => {
          setIsSubmitted(true);
        },
      });
    });
  };

  return (
    <NewsletterBand className="max-w-[598px]">
      <NewsletterBandCopy>
        <NewsletterBandTitle>
          {intl.formatMessage({
            id: 'component.server.page.footer.footerNewsletterCTA.newsletterDesktop',
            defaultMessage: 'Tady nesmíte chybět!',
          })}
        </NewsletterBandTitle>
        <NewsletterBandSubtitle>
          {intl.formatMessage({
            id: 'component.server.page.footer.footerNewsletterCTA.joinDesktop',
            defaultMessage: 'Odebírejte zprávy od RCprofíků a buďte v obraze!',
          })}
        </NewsletterBandSubtitle>
      </NewsletterBandCopy>

      <NewsletterBandForm noValidate onSubmit={handleSubmit(onSubmit)}>
        <NewsletterBandField>
          <NewsletterBandInput
            {...register('email')}
            type="email"
            variant={
              errors.email ? NewsletterBandInputVariant.INVALID : NewsletterBandInputVariant.BASE
            }
            data-slot="footer-newsletter-input"
            aria-label={intl.formatMessage({
              id: 'component.server.page.footer.footerNewsletterCTA.emailAriaLabel',
              defaultMessage: 'Přihlášení k odběru newsletteru',
            })}
            placeholder={intl.formatMessage({
              id: 'component.server.page.footer.footerNewsletterCTA.email',
              defaultMessage: 'Zadejte svůj e-mail',
            })}
          />
        </NewsletterBandField>
        <NewsletterBandSubmit
          variant={NewsletterBandSubmitVariant.FUSED}
          loading={isPending}
          disabled={isPending}
        >
          <div className="block lap:hidden">
            <IconChevronSend className="size-4" />
          </div>
          <div className="hidden uppercase lap:block">
            {intl.formatMessage({
              id: 'component.server.page.footer.footerNewsletterCTA.submit',
              defaultMessage: 'Odebírat',
            })}
          </div>
        </NewsletterBandSubmit>
      </NewsletterBandForm>

      <NewsletterBandConsent>
        {intl.formatMessage(
          {
            id: 'component.server.page.footer.footerNewsletterCTA.privacy',
            defaultMessage: 'Registrací potvrzujete souhlas s {gdpr}.',
          },
          {
            gdpr: (
              <Link
                key="gdpr"
                href={Url.personalDataProcessing}
                className="underline underline-offset-2"
              >
                {intl.formatMessage({
                  id: 'component.server.page.footer.footerNewsletterCTA.privacyGdpr',
                  defaultMessage: 'Podmínkami ochrany osobních údajů',
                })}
              </Link>
            ),
          }
        )}
      </NewsletterBandConsent>

      {isSubmitted && (
        <NewsletterBandNotice variant={NewsletterBandNoticeVariant.ACCEPTED}>
          {intl.formatMessage({
            id: 'component.server.page.footer.footerNewsletterCTA.success',
            defaultMessage: 'Potvrďte odběr novinek ve svém e-mailu',
          })}
        </NewsletterBandNotice>
      )}

      {errorMessage && (
        <NewsletterBandNotice variant={NewsletterBandNoticeVariant.REJECTED}>
          {errorMessage}
        </NewsletterBandNotice>
      )}
    </NewsletterBand>
  );
}
